import React, { useEffect, useState } from "react"
import { api, ApiError } from "../../lib/api"
import { useMe } from "../../hooks/useMe"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { Card } from "../ui/Card"
import { PageLoading, Spinner } from "../ui/Spinner"
import { useToast } from "../ui/Toast"

export default function Configuracoes() {
  const { me, loading, error, refetch } = useMe()
  const { toast } = useToast()

  const [name, setName] = useState("")
  const [savingName, setSavingName] = useState(false)
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (me) setName(me.user.name ?? "")
  }, [me])

  if (loading) {
    return (
      <AppShell>
        <PageLoading />
      </AppShell>
    )
  }

  if (error || !me) {
    return (
      <AppShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-navy-700">{error ?? "Erro ao carregar seus dados"}</p>
          <Button variant="secondary" onClick={refetch}>
            Tentar de novo
          </Button>
        </div>
      </AppShell>
    )
  }

  const nameChanged = name.trim() !== (me.user.name ?? "").trim()

  async function saveName() {
    if (!name.trim() || savingName) return
    setSavingName(true)
    try {
      await api.patch("/api/me", { user: { name } })
      toast("Nome atualizado.", "success")
      refetch()
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Não foi possível salvar.", "error")
    } finally {
      setSavingName(false)
    }
  }

  async function resend() {
    setResending(true)
    try {
      await api.post("/api/me/resend_confirmation")
      setResent(true)
      toast("E-mail de confirmação reenviado.", "success")
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Não foi possível reenviar.", "error")
    } finally {
      setResending(false)
    }
  }

  async function deleteAccount() {
    setDeleting(true)
    try {
      await api.del("/api/me")
      window.location.href = "/"
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Não foi possível excluir a conta.", "error")
      setDeleting(false)
    }
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Configurações</h1>
          <p className="mt-0.5 text-sm text-navy-500">Gerencie os dados da sua conta.</p>
        </div>

        {/* Conta */}
        <Card className="flex flex-col gap-5">
          <div>
            <label htmlFor="cfg-name" className="mb-1 block text-xs font-semibold uppercase tracking-wider text-navy-500">
              Nome
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="cfg-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm text-navy-900 outline-none focus:border-gold-400"
              />
              <Button variant="secondary" onClick={() => void saveName()} disabled={!nameChanged || !name.trim() || savingName}>
                {savingName && <Spinner className="h-4 w-4" />}
                Salvar
              </Button>
            </div>
          </div>

          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-navy-500">E-mail</p>
            <p className="text-sm text-navy-800">{me.user.email}</p>
            {me.confirmed ? (
              <p className="mt-1 text-xs font-medium text-emerald-600">E-mail confirmado ✓</p>
            ) : (
              <div className="mt-1.5 flex flex-wrap items-center gap-3">
                <span className="text-xs font-medium text-red-600">E-mail não confirmado</span>
                <button
                  type="button"
                  onClick={() => void resend()}
                  disabled={resending || resent}
                  className="text-xs font-semibold text-gold-600 underline disabled:no-underline disabled:text-navy-400"
                >
                  {resent ? "E-mail enviado ✓" : resending ? "Enviando..." : "Reenviar confirmação"}
                </button>
              </div>
            )}
          </div>
        </Card>

        {/* Zona de perigo */}
        <Card className="border-red-200!">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-red-600">Excluir conta</h2>
          <p className="mt-2 text-sm text-navy-600">
            Apaga sua conta, seu plano e histórico de simulados. Essa ação é permanente e não pode ser desfeita.
          </p>
          {!confirmDelete ? (
            <Button variant="danger" className="mt-4" onClick={() => setConfirmDelete(true)}>
              Excluir minha conta
            </Button>
          ) : (
            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-700">Tem certeza? Isso apaga tudo permanentemente.</p>
              <div className="flex flex-wrap gap-3">
                <Button variant="danger" onClick={() => void deleteAccount()} disabled={deleting}>
                  {deleting && <Spinner className="h-4 w-4 text-white" />}
                  Confirmar exclusão
                </Button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  )
}
